import type { CalendarDayEntry, PersonalBestRecordItem } from '../services/api';

export interface FitnessPoint { date: string; ctl: number | null }
export interface RampWindow { days: number; rate: number | null; delta: number | null; points: FitnessPoint[] }
export const RAMP_WINDOWS = [7, 28] as const;
export function shiftDay(day: string, amount: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}
export function localToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
/** Ignore API placeholder metrics (id=0), future dates and non-finite values. */
export function fitnessProgress(days: CalendarDayEntry[], today: string) {
  const values = new Map<string, number>();
  for (const entry of days) {
    const metric = entry.today;
    if (!metric || !metric.id || !metric.calculation_date || metric.ctl == null || String(metric.ctl).trim() === '') continue;
    const date = metric.calculation_date.slice(0, 10);
    const ctl = Number(metric.ctl);
    if (validDay(date) && date <= today && Number.isFinite(ctl) && ctl >= 0) values.set(date, ctl);
  }
  const asOf = [...values.keys()].sort().at(-1) ?? null;
  const windows: RampWindow[] = RAMP_WINDOWS.map(period => {
    if (!asOf) return { days: period, rate: null, delta: null, points: [] };
    const baseline = values.get(shiftDay(asOf, -period));
    const delta = baseline == null ? null : values.get(asOf)! - baseline;
    return {
      days: period, delta, rate: delta == null ? null : delta * 7 / period,
      points: Array.from({ length: period + 1 }, (_, i) => {
        const date = shiftDay(asOf, i - period);
        return { date, ctl: values.get(date) ?? null };
      }),
    };
  });
  return { asOf, windows };
}
function validDay(day: string) {
  const date = new Date(`${day}T12:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === day;
}
export function peakKind(type: string) {
  const match = /^(power|heart_rate|pace|speed|cadence)(\d+)(sec|min)$/.exec(type);
  return match && Number(match[2]) > 0 ? { metric: match[1], duration: Number(match[2]), unit: match[3] } : null;
}
/** Recent entries, not fabricated all-time rankings or medals. */
export function recentPeakRows(records: PersonalBestRecordItem[], today?: string) {
  const seen = new Set<string>();
  const priority = (type: string) => {
    const kind = peakKind(type);
    return ['power', 'heart_rate', 'pace', 'speed', 'cadence'].indexOf(kind?.metric ?? '') * 100 + (['5sec', '1min', '5min', '20min', '60min'].indexOf(`${kind?.duration}${kind?.unit}`) + 1 || 90);
  };
  const valid = [...records].filter(row => peakKind(row.peak_type) && Number(row.value) > 0 && Number.isFinite(Number(row.value)) && typeof row.record_date === 'string' && validDay(row.record_date.slice(0, 10)) && (!today || (row.record_date.slice(0, 10) <= today && row.record_date.slice(0, 10) >= shiftDay(today, -27))))
    .sort((a, b) => b.record_date.slice(0, 10).localeCompare(a.record_date.slice(0, 10)) || priority(a.peak_type) - priority(b.peak_type) || b.id - a.id)
    .filter(row => {
      const key = `${row.peak_type}:${row.workout?.id ?? row.workout_id ?? row.record_date}`;
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
  // The API stores every workout peak. Select the top three per interval within
  // the requested window; never present ordinary peaks as all-time records.
  const best = new Set<number>();
  const groups = new Map<string, PersonalBestRecordItem[]>();
  for (const row of valid) {
    const key = `${row.peak_type}:${row.workout?.workout_type?.name ?? ''}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  for (const rows of groups.values()) {
    const type = rows[0].peak_type;
    rows.sort((a, b) => (Number(a.value) - Number(b.value)) * (type.startsWith('pace') ? 1 : -1) || b.record_date.localeCompare(a.record_date));
    rows.slice(0, 3).forEach(row => best.add(row.id));
  }
  return valid.filter(row => best.has(row.id));
}
/** Missing days break the line; they must never be interpolated as zero CTL. */
export function fitnessSparkline(points: FitnessPoint[], width = 120, height = 32) {
  const valid = points.flatMap(p => p.ctl == null ? [] : [p.ctl]);
  if (!valid.length) return '';
  const min = Math.min(...valid), max = Math.max(...valid), span = Math.max(1, max - min);
  let penDown = false;
  return points.map((point, index) => {
    if (point.ctl == null) { penDown = false; return ''; }
    const x = 2 + index / Math.max(1, points.length - 1) * (width - 4);
    const y = 2 + (1 - (point.ctl - min) / span) * (height - 4);
    const command = penDown ? 'L' : 'M'; penDown = true;
    return `${command}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}
